// 10.09.25 16:55 - Enhanced goals system with NDIS/personal categories
import React, { useState, useEffect } from 'react';
import { collection, addDoc, onSnapshot, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import './GoalsPage.css';

const GoalsPage = () => {
  const { currentUser } = useAuth();
  const [goals, setGoals] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    category: 'personal',
    priority: 'medium',
    targetDate: ''
  });

  // 10.09.25 16:56 - Load goals from Firestore
  useEffect(() => {
    if (!currentUser?.email) return;
    
    const unsubscribe = onSnapshot(
      collection(db, 'Users', currentUser.email, 'Goals'),
      (snapshot) => {
        const goalsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdDate: doc.data().createdDate?.toDate(),
          targetDate: doc.data().targetDate?.toDate(),
          lastReviewDate: doc.data().lastReviewDate?.toDate()
        }));
        setGoals(goalsData);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // 10.09.25 16:57 - Add new goal function
  const handleAddGoal = async (e) => {
    e.preventDefault();
    if (!newGoal.title.trim()) return;

    try {
      await addDoc(collection(db, 'Users', currentUser.email, 'Goals'), {
        ...newGoal,
        status: 'active',
        createdDate: new Date(),
        targetDate: newGoal.targetDate ? new Date(newGoal.targetDate) : null,
        lastReviewDate: new Date(),
        nextReviewDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000), // 3 months
        extractedFromPlan: false,
        progressNotes: [],
        strategies: []
      });
      
      setNewGoal({ title: '', description: '', category: 'personal', priority: 'medium', targetDate: '' });
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding goal:', error);
    }
  };

  // 10.09.25 16:58 - Filter goals by category
  const filteredGoals = activeCategory === 'all' 
    ? goals 
    : goals.filter(goal => goal.category === activeCategory);

  return (
    <div className="goals-page">
      <header className="page-header">
        <h1>My Goals</h1>
        <button 
          className="add-goal-btn"
          onClick={() => setShowAddModal(true)}
        >
          Add New Goal
        </button>
      </header>

      {/* 10.09.25 16:59 - Category filter tabs */}
      <div className="category-tabs">
        <button 
          className={activeCategory === 'all' ? 'active' : ''}
          onClick={() => setActiveCategory('all')}
        >
          All Goals ({goals.length})
        </button>
        <button 
          className={activeCategory === 'ndis' ? 'active' : ''}
          onClick={() => setActiveCategory('ndis')}
        >
          NDIS Goals ({goals.filter(g => g.category === 'ndis').length})
        </button>
        <button 
          className={activeCategory === 'personal' ? 'active' : ''}
          onClick={() => setActiveCategory('personal')}
        >
          Personal Goals ({goals.filter(g => g.category === 'personal').length})
        </button>
      </div>

      {/* 10.09.25 17:00 - Goals list */}
      <div className="goals-container">
        {filteredGoals.length === 0 ? (
          <div className="empty-state">
            <p>No goals yet. Start by adding your first goal!</p>
          </div>
        ) : (
          <div className="goals-grid">
            {filteredGoals.map(goal => (
              <GoalCard key={goal.id} goal={goal} />
            ))}
          </div>
        )}
      </div>

      {/* 10.09.25 17:01 - Add goal modal */}
      {showAddModal && (
        <AddGoalModal 
          newGoal={newGoal}
          setNewGoal={setNewGoal}
          onSubmit={handleAddGoal}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
};

// 10.09.25 17:02 - Goal card component
const GoalCard = ({ goal }) => (
  <div className={`goal-card ${goal.category}`}>
    <div className="goal-header">
      <span className={`category-badge ${goal.category}`}>
        {goal.category === 'ndis' ? 'NDIS' : 'Personal'}
      </span>
      <span className={`priority-badge ${goal.priority}`}>
        {goal.priority}
      </span>
    </div>
    <h3>{goal.title}</h3>
    <p>{goal.description}</p>
    {goal.targetDate && (
      <div className="target-date">
        Target: {goal.targetDate.toLocaleDateString()}
      </div>
    )}
    <div className="goal-actions">
      <button className="update-btn">Update Progress</button>
      <button className="view-btn">View Details</button>
    </div>
  </div>
);

// 10.09.25 17:03 - Add goal modal component
const AddGoalModal = ({ newGoal, setNewGoal, onSubmit, onClose }) => (
  <div className="modal-overlay">
    <div className="modal">
      <h2>Add New Goal</h2>
      <form onSubmit={onSubmit}>
        <input
          type="text"
          placeholder="Goal title"
          value={newGoal.title}
          onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
          required
        />
        <textarea
          placeholder="Goal description"
          value={newGoal.description}
          onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
          rows="3"
        />
        <select
          value={newGoal.category}
          onChange={(e) => setNewGoal({...newGoal, category: e.target.value})}
        >
          <option value="personal">Personal Goal</option>
          <option value="ndis">NDIS Goal</option>
        </select>
        <select
          value={newGoal.priority}
          onChange={(e) => setNewGoal({...newGoal, priority: e.target.value})}
        >
          <option value="low">Low Priority</option>
          <option value="medium">Medium Priority</option>
          <option value="high">High Priority</option>
        </select>
        <input
          type="date"
          value={newGoal.targetDate}
          onChange={(e) => setNewGoal({...newGoal, targetDate: e.target.value})}
        />
        <div className="modal-actions">
          <button type="submit">Add Goal</button>
          <button type="button" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  </div>
);

export default GoalsPage;

